'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { TIMING } from '../../lib/constants';

interface ContinueButtonProps {
  onTap: () => void;
  delayMs?: number;
  disabled?: boolean;
  label?: string;
  /**
   * Controlled reveal — the parent-owned latch (Codex r3 P1). When provided
   * it gates interactivity so ALL of a screen's actions flip together; the
   * button still reports its own entrance completion via `onRevealed`.
   * Omitted → the internal latch gates (standalone usage, e.g. galleries).
   */
  revealed?: boolean;
  /** Fires when the entrance fade completes (feeds the parent latch). */
  onRevealed?: () => void;
}

/**
 * The reflection loop's primary action — the filled-pill species shared with
 * the text-mode Submit button (action-hierarchy re-tier, 2026-07-18), so the
 * app keeps one filled-primary family and the pill is the only accent-colored
 * action on the reflecting screen. Fill is `bg-accent-dark` (#247272):
 * cream-on-accent is 3.80:1 which fails AA for the 17px label; accent-dark
 * passes at 5.28:1 (Codex r1 P1).
 *
 * Structure: the entrance fade animates a WRAPPER, never the button itself —
 * framer writes inline `opacity` on whatever it animates, which would defeat
 * the button's own `disabled:opacity-50` state styling (inline beats class).
 *
 * Interactivity is driven by the SAME animation lifecycle as visibility
 * (Codex r1 P1): `revealed` latches on the entrance animation's completion,
 * never on a wall-clock timer — under rAF throttling a timer could make the
 * button focusable while framer still held it at opacity 0. While unrevealed
 * the button is out of the tab order, hidden from assistive tech, and
 * pointer-inert: an invisible primary must never be interactive.
 */
export function ContinueButton({
  onTap,
  delayMs = TIMING.CONTINUE_DELAY,
  disabled = false,
  label = 'Continue',
  revealed,
  onRevealed,
}: ContinueButtonProps) {
  // Latches true when the entrance fade completes; spurious later
  // onAnimationComplete fires (e.g. whileTap settling) are harmless because
  // the latch only ever sets true.
  const [internalRevealed, setInternalRevealed] = useState(false);
  const isRevealed = revealed ?? internalRevealed;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: delayMs / 1000, duration: 0.4 }}
      onAnimationComplete={() => {
        setInternalRevealed(true);
        onRevealed?.();
      }}
      whileTap={isRevealed && !disabled ? { scale: 0.97 } : undefined}
      className={isRevealed ? undefined : 'pointer-events-none'}
    >
      <button
        type="button"
        onClick={onTap}
        disabled={disabled}
        data-testid="continue-button"
        tabIndex={isRevealed ? 0 : -1}
        aria-hidden={isRevealed ? undefined : true}
        className="group flex items-center gap-2 rounded-[12px] bg-accent-dark px-9 py-3.5 font-body text-[17px] font-semibold tracking-[0.01em] text-[var(--cream)] cursor-pointer enabled:hover:shadow-[0_4px_14px_rgba(36,114,114,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-shadow duration-200"
        onKeyDown={(e) => {
          if (disabled || !isRevealed) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTap();
          }
        }}
      >
        {label}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          // Hover feedback only while genuinely pressable (Codex r1 P3): a
          // disabled pill must not advertise interactivity.
          className={
            disabled
              ? 'transition-transform duration-200'
              : 'transition-transform duration-200 group-hover:translate-x-[3px]'
          }
        >
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      </button>
    </motion.div>
  );
}
